import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Play } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0 }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

export default function ProductsPage() {
  const [products, setProducts] = useState([]);
  const [activeCategory, setActiveCategory] = useState("All");

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await axios.get(`${API}/products`);
        setProducts(response.data);
      } catch (error) {
        console.error("Error fetching products:", error);
      }
    };
    fetchProducts();
  }, []);

  const categories = ["All", ...new Set(products.map(p => p.category))];
  
  const filteredProducts = activeCategory === "All" 
    ? products 
    : products.filter(p => p.category === activeCategory);

  return (
    <div data-testid="products-page">
      {/* Hero */}
      <section className="relative h-[50vh] min-h-[400px] flex items-center">
        <div className="absolute inset-0">
          <img 
            src="https://images.pexels.com/photos/5304094/pexels-photo-5304094.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
            alt="Luxury yacht"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0E2954]/90 to-[#0E2954]/60" />
        </div>
        <div className="container-custom relative z-10">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
          >
            <motion.p variants={fadeInUp} className="label-style text-white/70 mb-4">
              Premium Products
            </motion.p>
            <motion.h1 variants={fadeInUp} className="text-4xl md:text-6xl text-white max-w-2xl">
              From Souvenirs to Luxury Ships
            </motion.h1>
          </motion.div>
        </div>
      </section>

      {/* Products */}
      <section className="section-padding">
        <div className="container-custom">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
          >
            <motion.div variants={fadeInUp} className="max-w-3xl mb-12">
              <h2 className="text-3xl md:text-4xl mb-6">
                Authentic Indonesian Products
              </h2>
              <p className="text-[#666666] text-lg leading-relaxed">
                Discover our curated collection of premium products, from handcrafted souvenirs 
                to luxury vessels for your island adventures.
              </p>
            </motion.div>

            {/* Category Filter */}
            <motion.div variants={fadeInUp} className="flex flex-wrap gap-3 mb-12">
              {categories.map((category) => (
                <button
                  key={category}
                  data-testid={`filter-${category.toLowerCase()}`}
                  onClick={() => setActiveCategory(category)}
                  className={`px-6 py-2 rounded-full text-sm font-medium transition-colors ${
                    activeCategory === category
                      ? "bg-[#0E2954] text-white"
                      : "bg-[#E5E5E5] text-[#666666] hover:bg-[#0E2954]/10"
                  }`}
                >
                  {category}
                </button>
              ))}
            </motion.div>

            {/* Products Grid */}
            <motion.div 
              variants={staggerContainer}
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
            >
              {filteredProducts.map((product) => (
                <motion.div
                  key={product.id}
                  variants={fadeInUp}
                  data-testid={`product-detail-${product.id}`}
                  className="group"
                >
                  <div className="relative overflow-hidden rounded-lg mb-4">
                    <img 
                      src={product.image_url}
                      alt={product.name}
                      className="w-full aspect-[4/3] object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute top-4 left-4">
                      <span className="bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full text-xs font-medium text-[#0E2954]">
                        {product.category}
                      </span>
                    </div>
                    {product.video_url && (
                      <a 
                        href={product.video_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`product-video-${product.id}`}
                        className="absolute bottom-4 right-4 bg-[#D92525] text-white px-4 py-2 rounded-full text-sm font-medium flex items-center gap-2 hover:bg-[#b81f1f] transition-colors"
                      >
                        <Play className="w-4 h-4" />
                        Watch Video
                      </a>
                    )}
                  </div>
                  <h3 className="text-xl mb-2">{product.name}</h3>
                  <p className="text-[#666666] text-sm mb-3 line-clamp-2">
                    {product.description}
                  </p>
                  <p className="text-[#1B4D3E] font-semibold">{product.price_range}</p>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Custom Orders */}
      <section className="section-padding bg-[#1B4D3E]">
        <div className="container-custom">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="grid md:grid-cols-2 gap-12 items-center"
          >
            <div>
              <h2 className="text-3xl md:text-4xl text-white mb-6">
                Looking for Something Special?
              </h2>
              <p className="text-white/70 leading-relaxed mb-8">
                We offer custom sourcing and bespoke products for unique requirements. 
                Whether you need custom-built vessels, bulk orders for your business, 
                or exclusive artisan pieces, our team can help.
              </p>
              <Link 
                to="/membership"
                data-testid="products-custom-order-btn"
                className="btn-primary"
              >
                Request Custom Order
              </Link>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <img 
                src="https://images.pexels.com/photos/3601453/pexels-photo-3601453.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Traditional boat"
                className="w-full aspect-square object-cover rounded-lg"
              />
              <img 
                src="https://images.unsplash.com/photo-1570435930547-7c3956003375?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHwxfHxpbmRvbmVzaWFuJTIwc291dmVuaXJzfGVufDB8fHx8MTc3NDMzMTQ5N3ww&ixlib=rb-4.1.0&q=85"
                alt="Indonesian crafts"
                className="w-full aspect-square object-cover rounded-lg mt-8"
              />
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
